import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Briefcase, Clock, Star, AlertCircle, Loader2, ChevronLeft, ChevronRight, Building2, Calendar, Tag, MapPin, X, Upload, CheckCircle } from "lucide-react";
import Navbar from '../components/Navbar';
import { Footer2 } from '../components/Footer2';
import { supabase } from '../lib/supabase';

const Careers = () => {
  // State management
  const [activeJobs, setActiveJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentActivePage, setCurrentActivePage] = useState(1);
  
  const jobsPerPage = 6;
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true, threshold: 0.1 });

  const [applyingTo, setApplyingTo] = useState(null); // job being applied to

  const formatRoleType = (roleType) => {
    const map = { volunteer: 'Volunteer', committee: 'Committee', executive: 'Executive', project_based: 'Project-Based' };
    return map[roleType] || roleType;
  };

  const formatLocationType = (locationType) => {
    const map = { in_person: 'On-site', remote: 'Remote', hybrid: 'Hybrid' };
    return map[locationType] || locationType;
  };

  // Fetch careers from database
  const fetchCareers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: jobs, error: fetchError } = await supabase
        .from('careers')
        .select('id, title, slug, description, role_type, location_type, time_commitment, positions_available, application_deadline, is_featured, tags, priority, published_at')
        .eq('status', 'published')
        .order('priority', { ascending: false })
        .order('published_at', { ascending: false });

      if (fetchError) throw fetchError;

      const now = new Date();
      const active = [];
      const closed = [];

      jobs?.forEach(job => {
        const deadline = job.application_deadline ? new Date(job.application_deadline) : null;

        const formattedJob = {
          id: job.id,
          title: job.title,
          category: formatRoleType(job.role_type),
          description: job.description || 'No description available.',
          locationType: formatLocationType(job.location_type),
          timeCommitment: job.time_commitment || 'Flexible',
          positions: job.positions_available || 1,
          deadline: deadline ? deadline.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Open until filled',
          is_featured: job.is_featured,
          tags: job.tags || [],
        };

        // Only include jobs without deadline or with future deadline
        if (!deadline || deadline >= now) {
          active.push(formattedJob);
        }
      });

      setActiveJobs(active);

    } catch (err) {
      console.error('Error fetching careers:', err);
      setError('Failed to load job postings. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch careers on component mount
  useEffect(() => {
    fetchCareers();
  }, []);

  // Pagination logic for active jobs
  const totalActivePages = Math.ceil(activeJobs.length / jobsPerPage);
  const activeStartIndex = (currentActivePage - 1) * jobsPerPage;
  const activeEndIndex = activeStartIndex + jobsPerPage;
  const currentActiveJobs = activeJobs.slice(activeStartIndex, activeEndIndex);

  const handleActivePageChange = (page) => {
    setCurrentActivePage(page);
    document.getElementById('active-jobs-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleActivePrevPage = () => {
    if (currentActivePage > 1) {
      handleActivePageChange(currentActivePage - 1);
    }
  };

  const handleActiveNextPage = () => {
    if (currentActivePage < totalActivePages) {
      handleActivePageChange(currentActivePage + 1);
    }
  };

  // Job Card Component
  const JobCard = ({ job, index, delay = 0, closed = false }) => (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: delay + index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[12px_12px_0px_0px_rgba(3,149,252,0.3)] transition-all duration-300 group flex flex-col"
    >
      {/* Header banner */}
      <div className="h-24 sm:h-32 bg-gradient-to-br from-[#0395fc] to-blue-700 flex items-center justify-center">
        <div className="text-center text-white">
          <Building2 className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-1 opacity-60" />
          <span className="text-xs font-semibold opacity-75 tracking-wide">DMLS</span>
        </div>
      </div>

      <div className="p-3 sm:p-6 flex flex-col flex-1">
        {/* Badges */}
        <div className="flex items-center justify-between mb-3">
          <span className="inline-block bg-[#0395fc] text-white px-2 py-1 text-xs font-bold uppercase tracking-wider border-2 border-black">
            {job.category}
          </span>
          {job.is_featured && (
            <span className="inline-block bg-yellow-400 text-black px-2 py-1 text-xs font-bold uppercase tracking-wider border-2 border-black">
              Featured
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-lg sm:text-xl font-black mb-3 group-hover:text-[#0395fc] transition-colors duration-300">
          {job.title}
        </h3>

        {/* Details */}
        <div className="space-y-1.5 mb-3 text-xs sm:text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <MapPin className="size-3 sm:size-4 flex-shrink-0" />
            <span>{job.locationType}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="size-3 sm:size-4 flex-shrink-0" />
            <span>{job.timeCommitment}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="size-3 sm:size-4 flex-shrink-0" />
            <span>Deadline: {job.deadline}</span>
          </div>
        </div>

        {/* Tags */}
        {job.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {job.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-0.5 text-xs font-medium border border-gray-300">
                <Tag className="size-3" />{tag}
              </span>
            ))}
            {job.tags.length > 3 && (
              <span className="inline-flex items-center bg-gray-100 text-gray-700 px-2 py-0.5 text-xs font-medium border border-gray-300">
                +{job.tags.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Description */}
        <p className="text-gray-700 mb-4 leading-relaxed text-sm flex-1">
          {job.description?.length > 120 ? `${job.description.substring(0, 120)}...` : job.description}
        </p>

        {/* Apply button */}
        {!closed ? (
          <motion.button
            onClick={() => setApplyingTo(job)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center justify-center gap-2 bg-[#0395fc] text-white px-4 py-2 sm:px-6 sm:py-3 font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 text-sm sm:text-base w-full"
          >
            Apply Now
            <Briefcase className="size-4" />
          </motion.button>
        ) : (
          <span className="inline-flex items-center justify-center gap-2 bg-gray-200 text-gray-500 px-4 py-2 font-bold border-2 border-gray-300 text-sm w-full cursor-not-allowed">
            Closed
          </span>
        )}
      </div>
    </motion.div>
  );

  // Application Modal
  const ApplicationModal = ({ job, onClose }) => {
    const [form, setForm] = useState({
      applicant_name: '', applicant_email: '', student_id: '',
      program: '', year_of_study: '', cover_letter: '', relevant_experience: '',
    });
    const [resumeFile, setResumeFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleField = (e) => {
      const { name, value } = e.target;
      setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!form.applicant_name || !form.applicant_email || !form.cover_letter) {
        setError('Name, email, and cover letter are required.');
        return;
      }
      setSubmitting(true);
      setError('');

      try {
        let resumeUrl = null;
        let resumeFilename = null;

        if (resumeFile) {
          const ext = resumeFile.name.split('.').pop();
          const path = `applications/${job.id}/${Date.now()}.${ext}`;
          const { error: uploadError } = await supabase.storage.from('resumes').upload(path, resumeFile);
          if (uploadError) throw new Error('Resume upload failed: ' + uploadError.message);
          resumeUrl = path;
          resumeFilename = resumeFile.name;
        }

        const { error: insertError } = await supabase.from('job_applications').insert([{
          career_id: job.id,
          applicant_name: form.applicant_name,
          applicant_email: form.applicant_email,
          student_id: form.student_id || null,
          program: form.program || null,
          year_of_study: form.year_of_study || null,
          cover_letter: form.cover_letter,
          relevant_experience: form.relevant_experience || null,
          resume_url: resumeUrl,
          resume_filename: resumeFilename,
        }]);

        if (insertError) throw new Error(insertError.message);
        setSubmitted(true);
      } catch (err) {
        setError(err.message || 'Submission failed. Please try again.');
      } finally {
        setSubmitting(false);
      }
    };

    const inputCls = 'w-full px-3 py-2.5 border-2 border-black rounded text-sm focus:outline-none focus:border-[#0395fc] transition-colors font-[var(--font-dm-sans)]';
    const labelCls = 'block text-sm font-bold mb-1 font-[var(--font-dm-sans)]';

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] w-full max-w-lg max-h-[90vh] flex flex-col"
        >
          {/* Modal header */}
          <div className="flex items-center justify-between p-6 border-b-2 border-black flex-shrink-0">
            <div>
              <h2 className="text-xl font-black font-[var(--font-anton)]">Apply — {job.title}</h2>
              <p className="text-xs text-muted-foreground font-[var(--font-dm-sans)] mt-0.5">Dalhousie Machine Learning Society</p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors ml-4 flex-shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>

          {submitted ? (
            <div className="flex flex-col items-center justify-center p-10 text-center gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center border-2 border-green-400">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-black font-[var(--font-anton)]">Application Submitted!</h3>
              <p className="text-sm text-muted-foreground font-[var(--font-dm-sans)]">
                We'll be in touch at <strong>{form.applicant_email}</strong>. Thank you for your interest in DMLS!
              </p>
              <button onClick={onClose}
                className="mt-2 px-6 py-2 bg-[#0395fc] text-white border-2 border-black rounded font-bold font-[var(--font-dm-sans)] shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all">
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border-2 border-red-400 rounded text-sm text-red-700 font-[var(--font-dm-sans)]">{error}</div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Full Name *</label>
                  <input name="applicant_name" value={form.applicant_name} onChange={handleField} className={inputCls} placeholder="Jane Smith" required />
                </div>
                <div>
                  <label className={labelCls}>Email *</label>
                  <input name="applicant_email" type="email" value={form.applicant_email} onChange={handleField} className={inputCls} placeholder="jane@dal.ca" required />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Student ID <span className="font-normal text-muted-foreground">(optional)</span></label>
                  <input name="student_id" value={form.student_id} onChange={handleField} className={inputCls} placeholder="B00123456" />
                </div>
                <div>
                  <label className={labelCls}>Year of Study <span className="font-normal text-muted-foreground">(optional)</span></label>
                  <select name="year_of_study" value={form.year_of_study} onChange={handleField} className={inputCls}>
                    <option value="">Select…</option>
                    {['1st Year','2nd Year','3rd Year','4th Year','Graduate','Other'].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls}>Program <span className="font-normal text-muted-foreground">(optional)</span></label>
                <select name="program" value={form.program} onChange={handleField} className={inputCls}>
                  <option value="">Select…</option>
                  {['Computer Science','Data Science','Machine Learning','Software Engineering','Information Technology','Other'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div>
                <label className={labelCls}>Cover Letter *</label>
                <textarea name="cover_letter" value={form.cover_letter} onChange={handleField} rows={4} className={inputCls} placeholder="Tell us why you're interested in this role and what you'd bring to DMLS..." required />
              </div>

              <div>
                <label className={labelCls}>Relevant Experience <span className="font-normal text-muted-foreground">(optional)</span></label>
                <textarea name="relevant_experience" value={form.relevant_experience} onChange={handleField} rows={3} className={inputCls} placeholder="Any relevant clubs, projects, or experience…" />
              </div>

              <div>
                <label className={labelCls}>Resume <span className="font-normal text-muted-foreground">(optional · PDF or Word · max 5 MB)</span></label>
                <label className="flex items-center gap-3 w-full px-3 py-2.5 border-2 border-dashed border-black rounded cursor-pointer hover:bg-gray-50 transition-colors">
                  <Upload className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                  <span className="text-sm font-[var(--font-dm-sans)] text-muted-foreground truncate">
                    {resumeFile ? resumeFile.name : 'Click to upload'}
                  </span>
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden"
                    onChange={e => setResumeFile(e.target.files[0] || null)} />
                </label>
              </div>

              <div className="pt-2 pb-1">
                <button type="submit" disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 bg-[#0395fc] text-white py-3 border-2 border-black rounded font-black font-[var(--font-anton)] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'SUBMIT APPLICATION'}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    );
  };

  // Pagination Component
  const PaginationControls = ({ 
    currentPage, 
    totalPages, 
    onPageChange, 
    onPrevPage, 
    onNextPage, 
    startIndex, 
    endIndex, 
    totalItems,
    label 
  }) => (
    <div className="container mx-auto px-4 max-w-6xl mt-12">
      <div className="flex flex-col items-center gap-6">
        {/* Page Info */}
        <div className="text-center">
          <p className="text-lg font-medium text-gray-700">
            Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} {label}
          </p>
        </div>
        
        {/* Pagination Buttons */}
        {totalPages > 1 && (
          <div className="flex items-center gap-4">
            {/* Previous Button */}
            <button
              onClick={onPrevPage}
              disabled={currentPage === 1}
              className={`flex items-center gap-2 px-4 py-2 font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 ${
                currentPage === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-black hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]'
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            
            {/* Page Numbers */}
            <div className="flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`w-10 h-10 font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 ${
                    currentPage === page
                      ? 'bg-[#0395fc] text-white'
                      : 'bg-white text-black hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            {/* Next Button */}
            <button
              onClick={onNextPage}
              disabled={currentPage === totalPages}
              className={`flex items-center gap-2 px-4 py-2 font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-200 ${
                currentPage === totalPages
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-black hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]'
              }`}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {applyingTo && <ApplicationModal job={applyingTo} onClose={() => setApplyingTo(null)} />}
      <Navbar />
      
      {/* Enhanced Hero Section */}
      <section ref={heroRef} className="pt-24 pb-16 bg-cover bg-center bg-no-repeat relative overflow-hidden" style={{ backgroundImage: 'url(/5.jpg)' }}>
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/50 z-0"></div>
        
        {/* Animated background elements */}
        <motion.div 
          className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-2xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 left-10 w-24 h-24 bg-yellow-300/20 rounded-full blur-xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <div className="container mx-auto px-4 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full border border-white/30 text-sm font-bold mb-6 backdrop-blur-sm"
              >
                <Briefcase className="size-4" />
                Career Opportunities
              </motion.div>

              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-5xl lg:text-6xl font-black mb-6 leading-tight"
              >
                Career Board
              </motion.h1>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-xl md:text-2xl mb-8 leading-relaxed text-white/90"
              >
                Discover exciting career opportunities in AI, machine learning, and data science. 
                From internships to full-time positions, find your next role here.
              </motion.p>
            </motion.div>

            {/* Right side - Career stats */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-white"
            >
              <div className="grid grid-cols-2 gap-6">
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="bg-white/20 backdrop-blur-sm p-6 border border-white/30 text-center"
                >
                  <div className="text-4xl font-black mb-2">{activeJobs.length}</div>
                  <div className="text-sm uppercase tracking-wider">Open Positions</div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="bg-white/20 backdrop-blur-sm p-6 border border-white/30 text-center"
                >
                  <div className="text-4xl font-black mb-2">6</div>
                  <div className="text-sm uppercase tracking-wider">Departments</div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="bg-white/20 backdrop-blur-sm p-6 border border-white/30 text-center"
                >
                  <div className="text-4xl font-black mb-2">100+</div>
                  <div className="text-sm uppercase tracking-wider">Active Members</div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.7 }}
                  className="bg-white/20 backdrop-blur-sm p-6 border border-white/30 text-center"
                >
                  <div className="text-4xl font-black mb-2">12</div>
                  <div className="text-sm uppercase tracking-wider">Leadership Roles</div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Active Jobs Section */}
      <section id="active-jobs-section" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black mb-6">Open Positions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore current career opportunities in AI, ML, and data science. 
              Apply early to increase your chances of landing your dream role!
            </p>
          </motion.div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-[#0395fc]" />
                <span className="text-lg font-medium text-muted-foreground">Loading opportunities...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mx-auto max-w-md">
              <div className="flex items-center gap-3 mb-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
                <h3 className="font-bold text-red-800">Error Loading Jobs</h3>
              </div>
              <p className="text-red-700 mb-4">{error}</p>
              <button
                onClick={fetchCareers}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-medium"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Jobs Grid */}
          {!isLoading && !error && (
            <>
              {currentActiveJobs.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {currentActiveJobs.map((job, index) => (
                    <JobCard key={job.id || index} job={job} index={index} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-600 mb-2">No Open Positions</h3>
                  <p className="text-gray-500">
                    Check back soon for new opportunities! We regularly update our career board.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Active Jobs Pagination */}
          {!isLoading && !error && totalActivePages > 1 && (
            <PaginationControls
              currentPage={currentActivePage}
              totalPages={totalActivePages}
              onPageChange={handleActivePageChange}
              onPrevPage={handleActivePrevPage}
              onNextPage={handleActiveNextPage}
              startIndex={activeStartIndex}
              endIndex={activeEndIndex}
              totalItems={activeJobs.length}
              label="open positions"
            />
          )}
        </div>
      </section>

      {/* Enhanced Call to Action */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              Want to Post a Job?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Are you a company looking to hire talented students with AI/ML skills? 
              Partner with DMLS to reach our community of passionate learners.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-white text-black px-8 py-4 font-bold border-4 border-white hover:bg-gray-100 transition-all duration-200 rounded-lg"
            >
              Contact Us
            </motion.a>
            <motion.a
              href="https://airtable.com/appbzvXorTGCwSsy9/pagrrVOHqpocEY5IX/form"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-transparent text-white px-8 py-4 font-bold border-4 border-white hover:bg-white hover:text-black transition-all duration-200 rounded-lg"
            >
              Partner With Us
            </motion.a>
          </motion.div>
        </div>
      </section>

      <Footer2 />
    </div>
  );
};

export default Careers;
